import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { MedContesto, MedRegola, MedRuolo, MedStatoUso, MedSuggerimento } from '../../api/types'
import { formatBytes } from '../../attributes'
import { useAuth } from '../../auth/AuthContext'
import { livelloModulo } from '../../auth/permessi'
import { useAsync } from '../../hooks/useAsync'
import { localeCorrente, t } from '../../i18n/lingua'
import { CountSeries, HeatmapSettimana } from '../charts'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { SelettoreAnagrafica } from '../SelettoreAnagrafica'
import { PosizioneDichiarata, testoDichiarata } from './Disponibilita'

/**
 * CENSO MED (D4): where a device is, whom it talks to, how much it is used.
 * The pieces of the device page and of the three views (Dove sono,
 * Collegamenti, Utilizzo). The data come from the hourly snapshot
 * (`med_insight_service`); a suggestion is written only when a person accepts it.
 */

export function etichettaRuolo(ruolo: MedRuolo | string): string {
  const e: Record<string, string> = {
    pacs: t('PACS (immagini DICOM)'),
    dicom_client: t('Riceve immagini DICOM'),
    hl7: t('Integrazione HL7'),
    medical: t('Altro elettromedicale (centrale, gateway)'),
    network: t('Servizi di rete'),
    it: t('Server o PC'),
    external: t('Indirizzo esterno sconosciuto'),
    unknown: t('Indirizzo interno senza CI'),
  }
  return e[ruolo] ?? ruolo
}

function tonoRuolo(ruolo: string): string {
  return ruolo === 'external' ? 'error' : ruolo === 'unknown' ? 'warn' : ruolo === 'pacs' || ruolo === 'hl7' ? 'info' : 'muted'
}

export function etichettaStatoUso(stato: MedStatoUso | string): string {
  return stato === 'active' ? t('Attivo') : stato === 'idle' ? t('Fermo') : t('Mai visto nei flussi')
}

export function BadgeUso({ stato }: { stato: string }) {
  const tono = stato === 'active' ? 'ok' : stato === 'idle' ? 'warn' : 'muted'
  return <span className={`badge badge--${tono}`}>{etichettaStatoUso(stato)}</span>
}

function etichettaFonte(fonte: string): string {
  const e: Record<string, string> = {
    rule: t('regola di posizione'),
    port_alias: t('descrizione della porta dello switch'),
    vlan_name: t('nome della VLAN'),
    reference: t('inventario di riferimento'),
    hl7: t('struttura HL7'),
    hl7_location: t('posizione HL7 (reparto^stanza^letto)'),
    monitor: t('rete dei monitor (centrale o monitor)'),
  }
  return e[fonte] ?? fonte
}

export function etichettaCampoSugg(campo: string): string {
  const e: Record<string, string> = {
    org_unit_id: t('Reparto'), location_id: t('Sede'), room: t('Stanza'), bed: t('Letto'),
  }
  return e[campo] ?? campo
}

function useScrive(): boolean {
  const { principal } = useAuth()
  return livelloModulo(principal, 'medical') === 'write'
}

/** «Dove» on the device page: the switch port, what the device says, the suggestions. */
export function DoveDispositivo({ deviceId, c, onCambiato }: { deviceId: string; c: MedContesto; onCambiato: () => void }) {
  const scrive = useScrive()
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const w = c.where

  async function accetta(s: MedSuggerimento) {
    setErrore(null)
    setLavoro(true)
    try {
      const r = await api.medWhereAccept([{ device_id: deviceId, field: s.field, value: s.value, entry_id: s.entry_id, source: s.source }])
      if (r.errors.length) throw new Error(r.errors[0].error)
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  if (!w) {
    return <EmptyState title={t('Posizione non ancora calcolata.')} hint={t('CENSO la ricalcola ogni ora; «Ricalcola adesso» nella vista Dove sono la calcola subito.')} />
  }
  return (
    <>
      <Card title={t('Dove lo vede la rete')}>
        <dl className="kv">
          <dt>{t('Posizione dichiarata dal dispositivo')}</dt>
          <dd><PosizioneDichiarata p={w.declared} /></dd>
          <dt>{t('Switch e porta')}</dt>
          <dd>{w.switch_ci_id
            ? <>{w.switch_name} · <span className="mono">{w.port}</span>{w.port_seen_at && <small className="text-muted"> · {formatDateTime(w.port_seen_at)}</small>}</>
            : <span className="text-muted">{t('nessuno switch ha imparato il suo MAC')}</span>}</dd>
          <dt>{t('Descrizione della porta')}</dt><dd>{w.port_alias || '—'}</dd>
          <dt>{t('VLAN')}</dt><dd>{w.vlan ?? '—'}{w.vlan_name ? ` · ${w.vlan_name}` : ''}</dd>
          <dt>{t('Struttura HL7')}</dt><dd>{w.hl7_facility || '—'}</dd>
          <dt>{t('AE title DICOM')}</dt><dd className="mono">{(w.dicom_ae ?? []).join(', ') || '—'}</dd>
          <dt>{t('Inventario di riferimento')}</dt><dd>{w.reference_location || '—'}</dd>
          {w.monitor && (
            <>
              <dt>{t('Rete dei monitor')}</dt>
              <dd>
                {w.monitor.protocol === 'carescape' ? 'GE CARESCAPE' : w.monitor.protocol === 'intellivue' ? 'Philips IntelliVue' : w.monitor.protocol}
                {[w.monitor.unit, w.monitor.room, w.monitor.bed].some(Boolean) && <> · {[w.monitor.unit, w.monitor.room, w.monitor.bed].filter(Boolean).join(' | ')}</>}
                {w.monitor.model && <> · {t('modello {m}', { m: w.monitor.model })}</>}
                {w.monitor.serial && <> · {t('seriale {s}', { s: w.monitor.serial })}</>}
                {w.monitor.software && <> · {t('software {v}', { v: w.monitor.software })}</>}
              </dd>
            </>
          )}
        </dl>
      </Card>
      <Card title={t('Suggerimenti')}>
        <ErrorBanner error={errore} />
        <DataTable
          rows={c.suggestions}
          rowKey={(s) => `${s.field}-${s.value}`}
          paginazione={false}
          strumenti={false}
          emptyText={t('Nessun suggerimento: la scheda dice già ciò che le fonti dicono, o le fonti non dicono niente.')}
          columns={[
            { key: 'field', header: t('Campo'), value: (s) => etichettaCampoSugg(s.field) },
            { key: 'value', header: t('Valore'), value: (s) => s.value, render: (s) => <strong>{s.value}</strong> },
            { key: 'source', header: t('Da dove'), value: (s) => etichettaFonte(s.source),
              render: (s) => <>{etichettaFonte(s.source)} {s.detail && <small className="text-muted">«{s.detail}»</small>}</> },
            { key: 'confidence', header: t('Affidabilità'), value: (s) => (s.confidence === 'high' ? t('alta') : t('media')) },
            ...(scrive ? [{
              key: 'azione', header: '', sort: false, searchable: false,
              render: (s: MedSuggerimento) => (
                <button type="button" className="secondary" disabled={lavoro || (s.field.endsWith('_id') && !s.entry_id)}
                        onClick={() => void accetta(s)}>{t('Accetta')}</button>
              ),
            }] : []),
          ]}
        />
      </Card>
    </>
  )
}

/** «Collegamenti» on the device page: the peers of the last 7 days. */
export function CollegamentiDispositivo({ c }: { c: MedContesto }) {
  return (
    <DataTable
      rows={c.peers}
      rowKey={(p, i) => `${p.address}-${p.port}-${i}`}
      initialSort={{ key: 'bytes', dir: 'desc' }}
      conteggio={{ nome: t('collegamenti') }}
      emptyText={t('Nessun traffico negli ultimi 7 giorni nei flussi (NetFlow/IPFIX): può parlare solo dentro la sua VLAN.')}
      columns={[
        { key: 'role', header: t('Ruolo'), filter: 'select', value: (p) => etichettaRuolo(p.role),
          render: (p) => <span className={`badge badge--${tonoRuolo(p.role)}`}>{etichettaRuolo(p.role)}</span> },
        { key: 'peer', header: t('Con chi'), value: (p) => p.name || p.address,
          render: (p) => p.ci_id ? <>{p.name} <small className="text-muted mono">{p.address}</small></> : <span className="mono">{p.address}</span> },
        { key: 'direction', header: t('Verso'), value: (p) => (p.direction === 'out' ? t('chiama') : t('è chiamato')) },
        { key: 'port', header: t('Porta'), type: 'number', mono: true, value: (p) => p.port },
        { key: 'bytes', header: t('Traffico'), type: 'number', align: 'right', value: (p) => p.bytes, render: (p) => formatBytes(p.bytes) },
        { key: 'last_seen', header: t('Ultima volta'), type: 'date', value: (p) => p.last_seen ?? '', render: (p) => formatDateTime(p.last_seen) },
      ]}
    />
  )
}

/** The days of the week, Monday first, for the hour map. */
function giorniSettimana(): string[] {
  const lunedi = new Date(Date.UTC(2024, 0, 1))
  return Array.from({ length: 7 }, (_, i) =>
    new Date(lunedi.getTime() + i * 864e5).toLocaleDateString(localeCorrente(), { weekday: 'short', timeZone: 'UTC' }))
}

/** «Utilizzo» on the device page: 30 days of active hours and the week × hour map. */
export function UsoDispositivo({ c }: { c: MedContesto }) {
  const u = c.usage
  // The hour mask is in UTC: each bit is placed at the browser's local day and hour.
  const celle = useMemo(() => {
    const m = Array.from({ length: 7 }, () => new Array<number>(24).fill(0))
    for (const g of u.days) {
      const [a, mese, giorno] = g.day.split('-').map(Number)
      for (let h = 0; h < 24; h++) {
        if (!(g.hour_mask & (1 << h))) continue
        const locale = new Date(Date.UTC(a, mese - 1, giorno, h))
        m[(locale.getDay() + 6) % 7][locale.getHours()] += 1
      }
    }
    return m
  }, [u.days])
  return (
    <>
      <div className="wazuh-kpi">
        <StatCard label={t('Stato')} value={etichettaStatoUso(u.state)} tone={u.state === 'active' ? 'ok' : u.state === 'idle' ? 'warn' : undefined} />
        <StatCard label={t('Ore attive in 30 giorni')} value={u.active_hours_30d} />
        <StatCard label={t('Ore con DICOM o HL7')} value={u.clinical_hours_30d} />
        <StatCard label={t('Traffico in 30 giorni')} value={formatBytes(u.bytes_30d)} />
      </div>
      <p className="field__hint">
        {t('È attività di rete, non esami: un’ora è «attiva» con almeno {soglia} di traffico. Le ore con DICOM o HL7 sono quelle in cui il dispositivo ha scambiato immagini o messaggi clinici. «Mai visto nei flussi» non vuol dire fermo: il suo traffico può non passare da un router che esporta i flussi.', { soglia: formatBytes(u.threshold_bytes) })}
      </p>
      <Card title={t('Ore attive al giorno')}>
        <CountSeries points={u.days.map((g) => ({ date: g.day, value: g.hours }))} label={t('ore attive')}
                     emptyHint={t('Nessun giorno con traffico negli ultimi 30 giorni.')} />
      </Card>
      <Card title={t('Quando si usa (ultimi 30 giorni)')}>
        <HeatmapSettimana celle={celle} giorni={giorniSettimana()} unita={t('giorni attivi')}
                          emptyHint={t('Nessuna ora attiva negli ultimi 30 giorni.')} />
      </Card>
      {c.wire && (
        <Card title={t('Attività dal filo (DICOM/HL7)')}>
          <p className="field__hint">
            {t('Quante volte al giorno l’apparecchio si è fatto riconoscere sul filo coi suoi messaggi d’identità: una traccia d’uso senza leggere nulla dei pazienti. In tutto {n} letture.', { n: c.wire.total })}
            {c.wire.last_active && ` ${t('Ultima acquisizione: {q}.', { q: c.wire.last_active })}`}
          </p>
          <CountSeries points={c.wire.days.map((g) => ({ date: g.day, value: g.count }))} label={t('letture')}
                       emptyHint={t('Nessuna lettura ancora.')} />
          {c.wire.info && (
            <dl className="kv" style={{ marginTop: 12 }}>
              {c.wire.info.software && <><dt>{t('Versione software')}</dt><dd className="mono">{c.wire.info.software}</dd></>}
              {c.wire.info.installed && <><dt>{t('Installazione')}</dt><dd>{c.wire.info.installed}</dd></>}
              {c.wire.info.manufactured && <><dt>{t('Fabbricazione')}</dt><dd>{c.wire.info.manufactured}</dd></>}
              {c.wire.info.calibrated && <><dt>{t('Ultima calibrazione')}</dt><dd>{c.wire.info.calibrated}</dd></>}
              {c.wire.info.station && <><dt>{t('Stazione')}</dt><dd>{c.wire.info.station}</dd></>}
              {c.wire.info.department && <><dt>{t('Reparto')}</dt><dd>{c.wire.info.department}</dd></>}
              {c.wire.info.institution && <><dt>{t('Struttura')}</dt><dd>{c.wire.info.institution}</dd></>}
            </dl>
          )}
        </Card>
      )}
    </>
  )
}

/** The device page's three sections, from one request. */
export function useContesto(deviceId: string) {
  return useAsync(() => api.medInsight(deviceId), [deviceId])
}

// --- The views ------------------------------------------------------------------

export function VistaDove() {
  const scrive = useScrive()
  const dati = useAsync(() => api.medWhere(), [])
  const [scelti, setScelti] = useState<Set<string>>(new Set())
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState<string | null>(null)
  const righe = dati.data?.devices ?? []

  async function ricalcola() {
    setLavoro(t('Ricalcolo…'))
    setErrore(null)
    try {
      await api.medInsightRefresh()
      await dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(null)
    }
  }

  /** The best suggestion of each chosen device, per field (the first is the best). */
  async function accettaScelti() {
    const voci = righe.filter((r) => scelti.has(r.id)).flatMap((r) => {
      const prime = new Map<string, MedSuggerimento>()
      for (const s of r.suggestions) if (!prime.has(s.field) && (!s.field.endsWith('_id') || s.entry_id)) prime.set(s.field, s)
      return [...prime.values()].map((s) => ({ device_id: r.id, field: s.field, value: s.value, entry_id: s.entry_id, source: s.source }))
    })
    if (!voci.length) return
    if (!window.confirm(t('Accettare {n} suggerimenti su {m} dispositivi?', { n: voci.length, m: scelti.size }))) return
    setLavoro(t('Applico…'))
    setErrore(null)
    let fatte = 0
    try {
      for (let i = 0; i < voci.length; i += 200) {
        fatte += (await api.medWhereAccept(voci.slice(i, i + 200))).accepted
      }
      setEsito(t('Accettati {n} suggerimenti.', { n: fatte }))
      setScelti(new Set())
      await api.medInsightRefresh().catch(() => undefined)
      await dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(null)
    }
  }

  return (
    <>
      <div className="med-opzioni">
        {scrive && <button type="button" className="secondary" onClick={() => void ricalcola()} disabled={Boolean(lavoro)}>{t('Ricalcola adesso')}</button>}
        {dati.data?.computed_at && <span className="field__hint">{t('Calcolato il {quando}.', { quando: formatDateTime(dati.data.computed_at) })}</span>}
        {lavoro && <span className="field__hint">{lavoro}</span>}
      </div>
      {scrive && scelti.size > 0 && (
        <div className="med-barra-selezione">
          <strong>{t('{n} dispositivi scelti', { n: scelti.size })}</strong>
          <button type="button" onClick={() => void accettaScelti()} disabled={Boolean(lavoro)}>{t('Accetta i suggerimenti migliori')}</button>
          <button type="button" className="secondary" onClick={() => setScelti(new Set())}>{t('Deseleziona')}</button>
        </div>
      )}
      <ErrorBanner error={errore || dati.error} />
      {esito && <p className="field__hint" style={{ color: 'var(--ok)' }}>{esito}</p>}
      {dati.loading && !dati.data ? <Spinner /> : (
        <DataTable
          id="medical-where"
          rows={righe}
          rowKey={(r) => r.id}
          initialSort={{ key: 'name' }}
          conteggio={{ nome: t('dispositivi') }}
          nomeEsportazione="elettromedicali-dove"
          selezione={scrive ? { chiavi: scelti, onCambia: setScelti, id: (r) => r.id } : undefined}
          columns={[
            { key: 'name', header: t('Nome'), value: (r) => r.name, render: (r) => <Link to={`/medical/devices/${r.id}`}>{r.name || '—'}</Link> },
            { key: 'org_unit', header: t('Reparto'), filter: 'select', value: (r) => r.org_unit ?? '' },
            { key: 'location', header: t('Sede'), filter: 'select', value: (r) => r.location ?? '', hidden: true },
            { key: 'bed', header: t('Stanza e letto'), value: (r) => [r.room, r.bed].filter(Boolean).join(' · ') },
            { key: 'declared', header: t('Posizione dichiarata dal dispositivo'), value: (r) => testoDichiarata(r.declared),
              render: (r) => <PosizioneDichiarata p={r.declared} /> },
            { key: 'switch', header: t('Switch e porta'), value: (r) => (r.switch_name ? `${r.switch_name} ${r.port}` : ''),
              render: (r) => r.switch_name ? <>{r.switch_name} <span className="mono">{r.port}</span></> : <span className="text-muted">—</span> },
            { key: 'port_alias', header: t('Descrizione della porta'), value: (r) => r.port_alias },
            { key: 'vlan', header: t('VLAN'), filter: 'select', value: (r) => (r.vlan == null ? '' : `${r.vlan}${r.vlan_name ? ` · ${r.vlan_name}` : ''}`) },
            { key: 'hl7_facility', header: t('Struttura HL7'), value: (r) => r.hl7_facility, hidden: true },
            { key: 'reference_location', header: t('Inventario di riferimento'), value: (r) => r.reference_location, hidden: true },
            { key: 'monitor', header: t('Rete dei monitor'), hidden: true,
              value: (r) => (r.monitor ? [r.monitor.unit, r.monitor.room, r.monitor.bed].filter(Boolean).join(' | ') : '') },
            { key: 'suggestions', header: t('Suggerimenti'), value: (r) => r.suggestions.map((s) => `${etichettaCampoSugg(s.field)}: ${s.value}`).join(' · '),
              render: (r) => r.suggestions.length
                ? <ul className="med-cambi">{r.suggestions.slice(0, 3).map((s) => <li key={`${s.field}-${s.value}`}>{etichettaCampoSugg(s.field)}: <strong>{s.value}</strong></li>)}</ul>
                : <span className="text-muted">—</span> },
          ]}
        />
      )}
      <RegolePosizione onCambiate={() => void dati.reload()} />
    </>
  )
}

function etichettaTipoRegola(k: string): string {
  const e: Record<string, string> = {
    vlan: t('VLAN'), switch: t('Switch'), port_prefix: t('Porte dello switch che iniziano con'),
    hl7_facility: t('Struttura HL7'), alias_token: t('Descrizione della porta contiene'),
    reference_text: t('Inventario di riferimento contiene'),
  }
  return e[k] ?? k
}

function RegolePosizione({ onCambiate }: { onCambiate: () => void }) {
  const scrive = useScrive()
  const dati = useAsync(() => api.medLocationRules(), [])
  const anagrafiche = useAsync(() => api.medMasterData(), [])
  const [kind, setKind] = useState<MedRegola['kind']>('vlan')
  const [valore, setValore] = useState('')
  const [reparto, setReparto] = useState<string | null>(null)
  const [sede, setSede] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  async function crea() {
    setErrore(null)
    try {
      await api.medLocationRuleCreate({ kind, value: valore, org_unit_id: reparto, location_id: sede })
      setValore('')
      await dati.reload()
      onCambiate()
    } catch (e) {
      setErrore(e)
    }
  }

  async function togli(id: string) {
    if (!window.confirm(t('Togliere questa regola di posizione? Non si può annullare.'))) return
    setErrore(null)
    try {
      await api.medLocationRuleDelete(id)
      await dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  return (
    <Card title={t('Regole di posizione')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Una regola dice dove stanno i dispositivi che hanno qualcosa in comune: «la VLAN 210 è la Rianimazione», «le porte con BOX nella descrizione sono del Pronto soccorso». Diventa un suggerimento su ogni dispositivo a cui si applica, al prossimo calcolo; non scrive niente da sola.')}
      </p>
      <ErrorBanner error={errore || dati.error} />
      {scrive && (
        <div className="form-grid">
          <label className="field">
            <span className="field__label">{t('Se')}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as MedRegola['kind'])}>
              {(['vlan', 'alias_token', 'hl7_facility', 'reference_text', 'port_prefix'] as const).map((k) => (
                <option key={k} value={k}>{etichettaTipoRegola(k)}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">{t('Valore')}</span>
            <input type={kind === 'vlan' ? 'number' : 'text'} value={valore} onChange={(e) => setValore(e.target.value)} maxLength={200} />
          </label>
          <span className="field" />
          <div className="field">
            <span className="field__label">{t('Allora il reparto è')}</span>
            <SelettoreAnagrafica kind="org_unit" voci={anagrafiche.data?.org_unit ?? []} value={reparto} onChange={setReparto} />
          </div>
          <div className="field">
            <span className="field__label">{t('e la sede è')}</span>
            <SelettoreAnagrafica kind="location" voci={anagrafiche.data?.location ?? []} value={sede} onChange={setSede} />
          </div>
          <div className="field">
            <span className="field__label">&nbsp;</span>
            <button type="button" onClick={() => void crea()} disabled={!valore.trim() || (!reparto && !sede)}>{t('Aggiungi la regola')}</button>
          </div>
        </div>
      )}
      <DataTable
        rows={dati.data?.rules ?? []}
        rowKey={(r) => r.id}
        paginazione={false}
        strumenti={false}
        emptyText={t('Nessuna regola.')}
        columns={[
          { key: 'kind', header: t('Se'), value: (r) => etichettaTipoRegola(r.kind) },
          { key: 'value', header: t('Valore'), mono: true, value: (r) => r.value },
          { key: 'org_unit', header: t('Reparto'), value: (r) => r.org_unit?.name ?? '—' },
          { key: 'location', header: t('Sede'), value: (r) => r.location?.name ?? '—' },
          { key: 'created_by', header: t('Da'), value: (r) => r.created_by ?? '' },
          ...(scrive ? [{
            key: 'azione', header: '', sort: false, searchable: false,
            render: (r: MedRegola) => <button type="button" className="secondary" onClick={() => void togli(r.id)}>{t('Togli')}</button>,
          }] : []),
        ]}
      />
    </Card>
  )
}

export function VistaCollegamenti() {
  const dati = useAsync(() => api.medConnections(), [])
  const c = dati.data
  if (dati.loading && !c) return <Spinner />
  if (!c) return <ErrorBanner error={dati.error} />
  const ruoli: MedRuolo[] = ['pacs', 'hl7', 'medical', 'it', 'network', 'external', 'unknown']
  return (
    <>
      <p className="field__hint">
        {t('Con chi parlano gli elettromedicali negli ultimi 7 giorni, dai flussi (NetFlow/IPFIX). Il collegamento fisico è la porta dello switch, in «Dove sono».')}
        {c.computed_at && ` ${t('Calcolato il {quando}.', { quando: formatDateTime(c.computed_at) })}`}
      </p>
      <div className="wazuh-kpi">
        {ruoli.map((r) => (
          <StatCard key={r} label={etichettaRuolo(r)} value={c.by_role[r] ?? 0} hint={t('dispositivi')}
                    tone={r === 'external' && (c.by_role[r] ?? 0) > 0 ? 'error' : undefined} />
        ))}
        <StatCard label={t('Senza traffico nei flussi')} value={c.without_traffic} hint={t('dispositivi')} />
      </div>
      <Card title={t('Gli interlocutori')}>
        <DataTable
          id="medical-connections"
          rows={c.peers}
          rowKey={(p) => `${p.address}-${p.role}-${p.port}`}
          initialSort={{ key: 'devices', dir: 'desc' }}
          conteggio={{ nome: t('interlocutori') }}
          nomeEsportazione="elettromedicali-collegamenti"
          columns={[
            { key: 'role', header: t('Ruolo'), filter: 'select', value: (p) => etichettaRuolo(p.role),
              render: (p) => <span className={`badge badge--${tonoRuolo(p.role)}`}>{etichettaRuolo(p.role)}</span> },
            { key: 'peer', header: t('Interlocutore'), value: (p) => p.name || p.address,
              render: (p) => p.name ? <>{p.name} <small className="text-muted mono">{p.address}</small></> : <span className="mono">{p.address}</span> },
            { key: 'port', header: t('Porta'), type: 'number', mono: true, value: (p) => p.port },
            { key: 'devices', header: t('Dispositivi'), type: 'number', align: 'right', value: (p) => p.devices },
            { key: 'bytes', header: t('Traffico'), type: 'number', align: 'right', value: (p) => p.bytes, render: (p) => formatBytes(p.bytes) },
            { key: 'last_seen', header: t('Ultima volta'), type: 'date', value: (p) => p.last_seen ?? '', render: (p) => formatDateTime(p.last_seen) },
          ]}
        />
      </Card>
    </>
  )
}

export function VistaUso() {
  const scrive = useScrive()
  const dati = useAsync(() => api.medUsage(), [])
  const [soglia, setSoglia] = useState<string>('')
  const [errore, setErrore] = useState<unknown>(null)
  const u = dati.data
  if (dati.loading && !u) return <Spinner />
  if (!u) return <ErrorBanner error={dati.error} />
  const conta = (s: string) => u.devices.filter((d) => d.usage_state === s).length

  async function salvaSoglia() {
    setErrore(null)
    try {
      await api.medSettings({ threshold_bytes: Math.round(Number(soglia) * 1000) })
      setSoglia('')
      await dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  return (
    <>
      <div className="wazuh-kpi">
        <StatCard label={t('Attivi')} value={conta('active')} tone="ok" />
        <StatCard label={t('Fermi da {n} giorni', { n: u.idle_after_days })} value={conta('idle')} tone={conta('idle') ? 'warn' : 'ok'} />
        <StatCard label={t('Mai visti nei flussi')} value={conta('unknown')} />
      </div>
      <p className="field__hint">
        {t('Attività di rete negli ultimi 30 giorni, non esami. Un’ora è attiva con almeno {soglia} di traffico; «fermo» = nessuna ora attiva da {n} giorni; «mai visto nei flussi» = il suo traffico non passa da un router che esporta i flussi, non che sia fermo.', { soglia: formatBytes(u.threshold_bytes), n: u.idle_after_days })}
        {' '}{t('Le ore in cui l’elettromedicale è collegato a un sistema clinico con l’agente CENSO contano come attive anche senza flussi.')}
        {u.last_usage_day && ` ${t('Dati fino al {giorno}.', { giorno: u.last_usage_day })}`}
      </p>
      {scrive && (
        <div className="med-opzioni">
          <label className="field" style={{ margin: 0 }}>
            <span className="field__label">{t('Soglia di un’ora attiva (KB)')}</span>
            <input type="number" min={1} value={soglia} placeholder={String(Math.round(u.threshold_bytes / 1000))}
                   onChange={(e) => setSoglia(e.target.value)} />
          </label>
          <button type="button" className="secondary" onClick={() => void salvaSoglia()} disabled={!soglia || Number(soglia) < 1}>{t('Salva la soglia')}</button>
        </div>
      )}
      <ErrorBanner error={errore} />
      <DataTable
        id="medical-usage"
        rows={u.devices}
        rowKey={(d) => d.id}
        initialSort={{ key: 'active_hours_30d', dir: 'desc' }}
        conteggio={{ nome: t('dispositivi') }}
        nomeEsportazione="elettromedicali-utilizzo"
        columns={[
          { key: 'name', header: t('Nome'), value: (d) => d.name, render: (d) => <Link to={`/medical/devices/${d.id}#utilizzo`}>{d.name || '—'}</Link> },
          { key: 'model', header: t('Modello'), value: (d) => d.model },
          { key: 'org_unit', header: t('Reparto'), filter: 'select', value: (d) => d.org_unit ?? '' },
          { key: 'usage_state', header: t('Uso'), filter: 'select', value: (d) => etichettaStatoUso(d.usage_state), render: (d) => <BadgeUso stato={d.usage_state} /> },
          { key: 'active_hours_30d', header: t('Ore attive (30 g)'), type: 'number', align: 'right', value: (d) => d.active_hours_30d },
          { key: 'clinical_hours_30d', header: t('Ore con DICOM o HL7'), type: 'number', align: 'right', value: (d) => d.clinical_hours_30d },
          { key: 'bytes_30d', header: t('Traffico'), type: 'number', align: 'right', value: (d) => d.bytes_30d, render: (d) => formatBytes(d.bytes_30d) },
          { key: 'last_activity_at', header: t('Ultima attività'), type: 'date', value: (d) => d.last_activity_at ?? '', render: (d) => (d.last_activity_at ? d.last_activity_at.slice(0, 10) : '—') },
        ]}
      />
    </>
  )
}
